import {http} from "@/api/request.ts";
import {
    AddMessage,
    CourseApplyList,
    CourseBasicInfo,
    CourseCate,
    CourseCommentList,
    CourseDiscussList,
    CourseInfo,
    CourseList,
    DiscussReplyInfo,
    FakeLiveInfo,
    GroupMemberList,
    GroupMessageList,
    IncludeCourse,
    NoticeInfo,
    OpList,
    PlayAuth,
    RecordVideoList,
    ReelCate,
    TestIndex,
    RefreshAuth,
    SecretShareInfo,
    SectionInfo,
    SimpleRecordVideo,
    SimpleSection,
    StudyUserList,
    TestCate,
    TestQuestionList, TestSubCate,
    UploadAuth,
    UploadedCourseFileList,
    UploadParam,
    User, TestQuestionInfo, TestReelList
} from "@/types";

export class SystemService {

    /**
     * 課程分类
     */
    static getCourseCateList = async (params: {}) => {
        return http<CourseCate[]>({
            url: "/index/classify_list",
            method: 'POST',
            data: params,
            name: "课程分类"
        })
    }

    /**
     * 操作记录
     */
    static getOpRecordList = async (params: {
        page: number,
        pageSize: number,
        // 接口名称搜索
        content: string,
        // 操作开始时间
        start_time: number,
        // 操作结束时间
        end_time: number
    }) => {
        return http<OpList>({
            url: "/user/operate_record_list",
            method: 'POST',
            data: params,
            name: "操作记录"
        })
    }

    /**
     * 发送验证码
     * @param params
     */
    static sendCode = async (params: { mobile: string }) => {
        return http({
            url: "/user/send_code",
            method: "POST",
            data: params,
            name: "发送验证码"
        })
    }

    /**
     * 意见反馈
     * @param params
     */
    static feedback = async (params: {
        content: string,
        image: string
    }) => {
        return http({
            url: "/user/feedback",
            method: "POST",
            data: params,
            name: "反馈内容"
        })
    }

    /**
     * 获取协议
     */
    static getSystemInfo = async (params: {
        title: string
    }) => {
        return http({
            url: "/user/system_info",
            method: "POST",
            data: params,
            name: "获取协议信息"
        })
    }
}

export class UserService {

    /**
     * 登录
     * 测试账号：17767251558
     */
    static login = async (params: { phone: string, code: string, name: string }) => {
        return http<User>({
            url: '/user/login',
            method: 'POST',
            data: params,
            name: "登录"
        })
    }
}

export class VideoService {

    /**
     * 录播课视频管理列表
     */
    static getVideoList = async (
        params: {
            // 页码
            page: number,
            // 条数
            pageSize: number,
            // 上传时间开始，时间戳
            start_time?: number,
            // 上传时间结束，时间戳
            end_time?: number,
            // 文件名称搜索
            content: string,
        }) => {
        return http<RecordVideoList>({
            url: "/video/video_list",
            method: "POST",
            data: params,
            name: "获取录播课视频管理列表"
        })
    }

    /**
     * 录播课已上传视频删除
     * - 视频id，多个用逗号串联；格式：id1,id2,id3 （列表中的id，不是视频id）
     */
    static delVideo = async (params: { id: string }) => {
        return http({
            url: "/video/del_video",
            method: "POST",
            data: params,
            name: "录播课已上传视频删除"
        })
    }

    /**
     * 获取上传凭证
     * - video_data {@link UploadParam} video_data可单个，可批量；存在章节id时即代表为单视频上传
     * - id 章节id
     */
    static gainUploadAuth = async (params: { video_data: string, id?: number }) => {
        return http<UploadAuth[]>({
            url: "/video/gain_upload_auth",
            method: "POST",
            data: params,
            name: "获取上传凭证"
        })
    }

    /**
     * 刷新上传凭证
     * 视频ID, 多个用逗号隔开，格式：视频1ID,视频2ID,视频3ID
     */
    static refreshUploadAuth = async (params: { video_id: string }) => {
        return http<RefreshAuth>({
            url: "/video/refresh_upload_auth",
            method: "POST",
            data: params,
            name: "刷新上传凭证"
        })
    }

    /**
     * 获取播放凭证
     */
    static getPlayAuth = async (params: { video_id: string }) => {
        return http<PlayAuth>({
            url: "/video/get_playAuth",
            method: "POST",
            data: params,
            name: "获取播放凭证"
        })
    }

    /**
     * 录播课视频转码状态
     * 1=转码完成，0=转码未完成
     */
    static getVideoStatus = async (params: { video_id: string }) => {
        return http<number>({
            url: "/video/video_status",
            method: "POST",
            data: params,
            name: "获取录播课视频转码状态"
        })
    }

    /**
     * 创建腾讯云点播视频签名
     */
    static createSignature = async () => {
        return http<string>({
            url: "/video/create_signature",
            method: "POST",
            data: {},
            name: "创建腾讯云点播视频签名"
        })
    }

    /**
     * 伪直播章节视频上传
     * - 返回 视频id
     */
    static addVideo = async (params: {
        // 文件名
        file_name: string,
        // 视频文件id
        video_id: string,
        // 视频时长，秒
        duration: number,
        // 宽度
        width: number,
        // 高度
        height: number,
    }) => {
        return http<string>({
            url: "video/tencent_add_video",
            method: "POST",
            data: params,
            name: "伪直播章节视频上传"
        })
    }
}

export class CourseService {

    /**
     * 课程上架（缺少系列课的下架时间 不得早于包含的课程中的最早下架时间）
     */
    static courseIssue = async (params: {
        // 课程id
        course_id: number;
        // 封面图
        thumb_image: string;
        // 上架时间
        putaway_time: number;
        // 下架时间
        down_time: number;
        // 有效期，1=永久有效，固定时间有效，小于时间戳等于结课后多少天
        buy_valid_date?: number;
        // 平台内用户是否可以购买（限定报名），1=是，0=否
        is_sign: number;
        // 是否第一讲有免费试听，1=是，0=否（不存在）
        is_try?: number;
        // 系列课课程价格
        price?: number;
    }) => {
        return http({
            url: "/course/course_issue",
            method: "POST",
            data: params,
            name: "课程上架"
        });
    };

    /**
     * 课程列表
     */
    static getCourseList = async (
        params: {
            page: number, pageSize: number,
            // 类型：1=直播课，2=录播课，3=系列课，4=组合课
            type: number,
            // 	一级分类id
            cate_id?: number,
            // 二级分类id
            two_cate_id?: number,
            // 状态：0=下架，1=上架，2=待发布
            status?: number,
            // 课程id
            id?: number,
            // 是否为私密课：0=公开，1=私密课，2=联培课
            is_secret?: number,
            // 关键字搜索
            content?: string
        }) => {
        return http<CourseList>({
            url: "/course/course_list",
            method: 'POST',
            data: params,
            name: "获取课程列表"
        })
    }

    /**
     * 课程详情
     * - id 课程id
     */
    static getCourseInfo = async (params: { id: number }) => {
        return http<CourseInfo>({
            url: "/course/course_info",
            method: 'POST',
            data: params,
            name: "获取课程详情"
        })
    }

    /**
     * 课程创建
     * - type 1=直播课，2=录播课，3=系列课，4=组合课
     * - title 课程标题
     * - short_detail 课程简介
     * - content 简介图地址
     * - sign_time 组合课优惠描述，组合课必传
     * - duration 每讲时长（分钟），直播课时必传
     * - price 	课程价格（组合课时可不传）
     * - cate_id 课程分类id
     * - two_cate_id 二级分类标签id
     * - is_secret 课程定位：0=公开，1=私密课，2=联培课
     * - describe_info 优惠信息；如：[{“count”:1,”discount”:0.9},{“count”:3,”discount”:0.8}]
     * - is_fake 伪直播课程，0=否，1=是
     */
    static addCourse = async (params: {
        type: number, title: string, short_detail: string,
        content: string, sign_time: string, duration: number,
        price: string, cate_id: number, two_cate_id: number, is_secret: number,
        describe_info: string, is_fake: number
    }) => {
        return http<number>({
            url: "/course/add_course",
            method: 'POST',
            data: params,
            name: "课程创建"
        })
    }

    /**
     * 课程基本信息
     * - id 课程id
     */
    static getCourseBasicInfo = async (params: {
        id: number
    }) => {
        return http<CourseBasicInfo>({
            url: "/course/course_basic_info",
            method: "POST",
            data: params,
            name: "获取课程基本信息"
        })
    }

    /**
     * 课程信息修改
     */
    static saveCourse = async (params: {
        // 课程id
        id: number,
        // 1=直播课，2=录播课,3=系列课，4=组合课
        type: number,
        // 课程标题
        title: string,
        // 课程简介
        short_detail: string,
        // 简介图地址
        content: string,
        // 组合课优惠描述
        sign_time: string,
        // 每讲时长（分钟），修改直播课时必传
        duration: number,
        // 课程价格
        price: string,
        // 分类标签id
        cate_id: number,
        // 二级分类id
        two_cate_id: number,
        // 上架时间
        putaway_time: number,
        // 下架时间
        down_time: number,
        // 是否可以报名：0=否，1=是
        is_sign: number,
        // 是否存在试听：0=否，1=是
        is_try: number,
        // 购买后有效期，1=永久有效，固定时间有效，小于时间戳等于结课后多少天
        buy_valid_date: number,
    }) => {
        return http({
            url: "/course/save_course",
            method: "POST",
            data: params,
            name: "课程信息修改"
        })
    }

    /**
     * 课程公告添加/编辑
     */
    static addSaveNotice = async (params: {
        // 课程id，新增时必传
        course_id: number,
        // 公告id，修改时必传
        notice_id: number,
        // 公告标题
        title: string,
        // 公告内容
        content: string,
        // 公告图片，格式:图片地址1，地址2
        images: string,
    }) => {
        return http({
            url: "/course/add_save_notice",
            method: "POST",
            data: params,
            name: "课程公告添加/编辑"
        })
    }

    /**
     * 课程公告详情
     */
    static getNoticeInfo = async (params: {
        // 公告id
        notice_id: number,
    }) => {
        return http<NoticeInfo>({
            url: "/course/notice_info",
            method: "POST",
            data: params,
            name: "获取课程公告详情"
        })
    }

    /**
     * 公告删除
     */
    static delNotice = async (params: {
        // 课程id
        course_id: number,
        // 公告id
        notice_id: number,
    }) => {
        return http({
            url: "/course/notice_del",
            method: "POST",
            data: params,
            name: "删除公告"
        })
    }

    /**
     * 我的所有课程（系列课/组合课添加课程时用）
     */
    static getAllCourseList = async (params: {
        // 系列课/组合课课程id
        id: number,
        // 选择的课程类型，1=系列课，2=组合课
        type: number,
        // 标题搜索
        title?: string,
    }) => {
        return http<IncludeCourse[]>({
            url: "/course/all_course_list",
            method: "POST",
            data: params,
            name: "获取我的所有课程"
        })
    }

    /**
     * 系列课添加/删除课程接口
     * 已上架课程不允许操作
     */
    static addDelSeriesCourse = async (params: {
        // 系列课课程id
        id: number,
        // 对应的课程id
        // type=1时格式为：1,2,3（添加时需携带全部课程id）;
        // 当type=2时，参数只允许存在一个课程id
        course_id: string,
        // 	1=课程列表添加删除，2=课表删除
        type: number,
    }) => {
        return http({
            url: "/course/series_ac_course",
            method: "POST",
            data: params,
            name: "系列课添加/删除课程"
        })
    }

    /**
     * 组合课添加/删除课程
     * 已上架的课程不允许操作
     */
    static addDelGroupCourse = async (params: {
        // 组合课id
        id: number,
        // 1=添加，2=删除
        way: number,
        // 单门课的id
        group_id: number,
    }) => {
        return http({
            url: "/course/group_course_add_or_del",
            method: "POST",
            data: params,
            name: "组合课添加/删除课程"
        })
    }

    /**
     * 课程/章节列表——此接口仅供查询时使用
     * 传course_id的情况下，返回的为章节id和标题
     */
    static getCourseSectionList = async (params: {
        // 1=直播课，2=录播课
        type?: number,
        // 课程id
        course_id?: number,
        // 课程标题
        title?: string,
    }) => {
        return http<SimpleSection[]>({
            url: "/course/course_section_list",
            method: "POST",
            data: params,
            name: "获取课程/章节列表(仅查询使用)"
        })
    }

    /**
     * 课程评价列表
     */
    static getCourseCommentList = async (params: {
        page: number,
        pageSize: number,
        // 1=直播课，2=录播课
        type: number,
        // 课程id
        id: number,
        // 1=最新发布，2=点赞最多，3=评分最高
        way: number,
        // 评价关键字搜索
        content: string,
        // 用户昵称搜索
        user_name: string,
    }) => {
        return http<CourseCommentList>({
            url: "/course/course_comment_list",
            method: "POST",
            data: params,
            name: "获取课程评价列表"
        })
    }

    /**
     * 课程评价回复
     * - comment_id 评价id
     * - course_id 课程id
     * - content 回复内容
     */
    static replyCourseComment = async (params: {
        comment_id: number, course_id: number, content: string
    }) => {
        return http({
            url: "/course/reply_course_comment",
            method: "POST",
            data: params,
            name: "课程评价回复"
        })
    }

    /**
     * 课程讨论区列表
     */
    static getCourseDiscussList = async (params: {
        page: number,
        pageSize: number,
        // 课程类型，1=直播课，2=录播课
        type: number,
        // 课程id
        course_id: number,
        // 章节id
        section_id: number,
        // 1=最新发布，2=点赞最多，3=回复最多
        way: number,
        // 讨论关键字搜索
        content: string,
        // 用户昵称搜索
        user_name: string,
    }) => {
        return http<CourseDiscussList>({
            url: "/course/course_discuss_list",
            method: 'POST',
            data: params,
            name: "获取课程讨论列表"
        })
    }

    /**
     * 讨论详情
     * - page 页码
     * - pageSize 每页数量
     * - id 讨论（一级）id
     */
    static getDiscussReplyInfo = async (params: { page: number, pageSize: number, id: number }) => {
        return http<DiscussReplyInfo>({
            url: "/course/discuss_reply_info",
            method: 'POST',
            data: params,
            name: "获取讨论详情"
        })
    }

    /**
     * 讨论区回复
     * - id 要回复讨论id
     * - content 回复内容
     */
    static replyDiscuss = async (params: { id: number, content: string }) => {
        return http({
            url: "/course/reply_discuss",
            method: 'POST',
            data: params,
            name: "回复讨论"
        })
    }

    /**
     * 删除评价回复
     */
    static delCommentReply = async (params: { id: number, course_id: number }) => {
        return http({
            url: "/course/del_comment_reply",
            method: 'POST',
            data: params,
            name: "删除评价回复"
        })
    }

    /**
     * 讨论区删除
     * - id 要删除的讨论或回复id
     */
    static delDiscussReply = async (params: { id: number }) => {
        return http({
            url: "/course/del_discuss_reply",
            method: "POST",
            data: params,
            name: "删除讨论或回复"
        })
    }

    /**
     * 私密课信息
     * id - 课程id
     */
    static getSecretShareInfo = async (params: { id: number }) => {
        return http<SecretShareInfo>({
            url: "/course/secret_share_info",
            method: "POST",
            data: params,
            name: "获取私密课信息"
        })
    }

    /**
     * 删除课程
     * - id 课程id
     */
    static delCourse = async (params: { id: number }) => {
        return http({
            url: "/course/del_course",
            method: "POST",
            data: params,
            name: "删除课程"
        })
    }
}

export class SectionService {

    /**
     * 导学/讲义/巩固等文件上传
     * - 返回id
     */
    static addCourseFile = async (params: {
        // 文件名
        name: string,
        // 大小（字节）
        size: number,
        // 类型
        ext: string,
        // 文件地址
        url: string,
        info?: string,
    }) => {
        return http<number>({
            url: "/section/add_course_file",
            method: 'POST',
            data: params,
            name: "上传导学/讲义/巩固等文件"
        })
    }

    /**
     * 已上传讲义文件信息列表
     */
    static getUploadedFile = async (params: {
        page: number,
        pageSize: number,
        content: string
    }) => {
        return http<UploadedCourseFileList>({
            url: "/section/file_list",
            method: 'POST',
            data: params,
            name: "已上传讲义文件信息列表"
        })
    }

    /**
     * 录播课批量新增章节
     * @param params 课程id，文件
     */
    static batchAddRecordSection = async (params: FormData) => {
        return http<number>({
            url: "/section/batch_add_section",
            method: 'POST',
            data: params,
            name: "批量新增录播课章节"
        })
    }

    /**
     * 已上传录播课视频列表接口（仅供新增章节时添加视频用）
     */
    static getRecordVideoList = async (params: {
        // 文件名搜索
        content: string,
    }) => {
        return http<SimpleRecordVideo[]>({
            url: "/video/record_video_list",
            method: 'POST',
            data: params,
            name: "获取已上传录播课视频列表(新增章节添加视频用)"
        })
    }

    /**
     * 录播课新增章节
     * - 返回章节id
     */
    static addRecordSection = async (params: {
        // 课程id
        id: number,
        // 标题
        title: string,
        // 视频id
        video_id?: string,
        // 导学文件id
        ahead_data?: number,
        // 讲义文件id
        pdf_data?: number,
        // 巩固文件id
        consolidate_data?: number,
        // 是否设置观看时效，0=否，1=是
        is_playback?: number,
        // 开始时间，10位时间戳
        view_start_time?: number,
        // 结束时间，10位时间戳
        view_end_time?: number,
    }) => {
        return http<number>({
            url: "/section/add_record_section",
            method: 'POST',
            data: params,
            name: "录播课新增章节"
        })
    }

    /**
     * 录播课章节编辑接口
     * 只需传参修改项即可，不修改的字段不传
     * 时效设置使用此接口即可
     */
    static saveRecordSection = async (params: {
        // 课程id
        course_id: number,
        // 章节id
        section_id: number,
        // 标题
        title?: string,
        // 视频id
        video_id?: string,
        // 导学文件id
        ahead_data?: number,
        // 讲义文件id
        pdf_data?: number,
        // 巩固文件id
        consolidate_data?: number,
        // 是否回放，1=是，0=否
        is_playback?: number,
        // 回放观看开始时间
        view_start_time?: number,
        // 回放观看截止时间
        view_end_time?: number,
    }) => {
        return http({
            url: "/section/save_record_section",
            method: "POST",
            data: params,
            name: "编辑录播课章节信息"
        })
    }

    /**
     * 章节上下架操作
     * @param params
     */
    static upDownSection = async (params: {
        // 课程id
        course_id: number;
        // 章节id
        section_id: number;
        // 0=下架，1=上架
        status: number;
    }) => {
        return http({
            url: "/section/section_status",
            method: 'POST',
            data: params,
            name: "课程章节上下架"
        })
    }

    /**
     * 直播课新增章节
     * - 返回章节id
     */
    static addLiveSection = async (params: {
        // 课程id
        id: number,
        // 标题
        title: string,
        // 直播日期，0点10位时间戳
        live_date: number,
        // 开始时间，10位时间戳
        start_time: number,
        // 结束时间，10位时间戳
        end_time: number,
        // 伪直播上传时的视频id
        video_id?: string,
        // 导学文件id
        ahead_data?: number,
        // 讲义文件id
        pdf_data?: number,
        // 巩固文件id
        consolidate_data?: number
    }) => {
        return http<number>({
            url: "/section/add_live_section",
            method: 'POST',
            data: params,
            name: "直播课新增章节"
        })
    }

    /**
     * 直播课章节编辑
     * 原定时间的开播前2小时不允许修改视频
     * 原定时间的开播前1小时不允许上传视频
     * 只需传参修改项即可，不修改的字段不传
     * 回放设置使用此接口即可
     */
    static saveLiveSection = async (params: {
        // 课程id
        course_id: number,
        // 章节id
        section_id: number,
        // 标题
        title?: string,
        // 直播日期，0点10位时间戳
        live_date?: number,
        // 开始时间，10位时间戳
        start_time?: number,
        // 结束时间，10位时间戳
        end_time?: number,
        // 视频id
        video_id?: string,
        // 是否回放，1=是，0=否
        is_playback?: number,
        // 回放观看开始时间
        view_start_time?: number,
        // 回放观看截止时间
        view_end_time?: number,
        // 导学文件id
        ahead_data?: number,
        // 讲义文件id
        pdf_data?: number,
        // 巩固文件id
        consolidate_data?: number,
    }) => {
        return http({
            url: "/section/save_live_section",
            method: "POST",
            data: params,
            name: "更改直播课章节信息"
        })
    }

    /**
     * 章节详情
     * - course_id 课程id
     * - section_id 章节id
     */
    static getSectionInfo = async (params: { course_id: number, section_id: number }) => {
        return http<SectionInfo>({
            url: "/section/section_info",
            method: "POST",
            data: params,
            name: "更改前获取已有章节信息"
        })
    }

    /**
     * 删除章节
     * 只有课程的状态为未发布时才能删除章节，status=2
     * - course_id 课程id
     * - section_id 章节id
     */
    static delCourseSection = async (params: { course_id: number, section_id: number }) => {
        return http({
            url: "/section/del_section",
            method: "POST",
            data: params,
            name: "删除课程章节"
        })
    }

    /**
     * 章节学习记录
     */
    static getStudySectionList = async (params: {
        // 页码
        page: number,
        // 条数
        pageSize: number,
        // 章节id
        section_id: number,
        // 1=直播课，2=录播课
        course_type: number,
        // 用户手机号搜索
        phone: string,
        // 用户昵称搜索
        content: string,
    }) => {
        return http<StudyUserList>({
            url: "/study/study_section_list",
            method: 'POST',
            data: params,
            name: "获取章节学习记录"
        })
    }

    /**
     * AI直播开始上课
     * id -章节id
     */
    static startLiveClass = async (params: { id: number }) => {
        return http<FakeLiveInfo>({
            url: "/section/start_class",
            method: "POST",
            data: params,
            name: "AI直播开始上课"
        })
    }
}

export class MessageService {

    /**
     * 群聊消息发送
     */
    static addGroupMessage = async (params: {
        // 章节id
        id: number,
        // 消息体
        msg_body: string
    }) => {
        return http<AddMessage>({
            url: "/message/add_group_message",
            method: 'POST',
            data: params,
            name: "群聊消息发送"
        })
    }

    /**
     * 群聊历史消息记录
     */
    static getGroupMessageList = async (params: {
        // 章节id
        id: number;
        // 页码
        page: number;
        // 条数
        pageSize: number;
    }) => {
        return http<GroupMessageList>({
            url: "/message/group_chat_list",
            method: 'POST',
            data: params,
            name: "获取群聊历史消息记录"
        })
    }

    /**
     * - id 章节id
     * - msg_time 会话详情的最后一条消息（第一次请求时）/上一次轮询结果的最后一条消息时间；未有消息体时传0
     */
    static findGroupChat = async (params: {
        id: number;
        msg_time: number;
    }) => {
        return http<GroupMessageList>({
            url: "message/find_group_chat",
            method: 'POST',
            data: params,
            name: "轮询获取群聊消息"
        })
    }

    /**
     * 成员信息列表
     */
    static getGroupMemberList = async (params: {
        // 章节id
        id: number;
        // 页码
        page: number;
        // 条数
        pageSize: number;
    }) => {
        return http<GroupMemberList>({
            url: "/message/group_member_list",
            method: 'POST',
            data: params,
            name: "获取成员信息列表"
        })
    }

    /**
     * 群聊禁言/取消禁言设置
     */
    static banGroup = async (params: {
        // 章节id
        id: number;
        // 是否禁言，0=否，1=是
        is_banned: number;
    }) => {
        return http({
            url: "/message/group_banned",
            method: "POST",
            data: params,
            name: "群聊禁言/取消禁言设置"
        })
    }
}

export class OrderService {

    /**
     * 课程报名列表
     */
    static getCourseApplyList = async (params: {
        page: number,
        pageSize: number,
        // 课程id
        course_id: number,
        // 报名来源，1=用户购买，2=老师添加
        buy_source: number,
        // 用户昵称搜索
        u_content: string,
        // 用户手机号搜索
        m_content: string,
        // 课程类型，1=付费课，2=免费课
        is_pay: number,
        // 订单号搜索
        order: string,
        // 是否来自于接口，1=是，0=否
        is_api?: number,
    }) => {
        return http<CourseApplyList>({
            url: "/order/course_apply_list",
            method: 'POST',
            data: params,
            name: "获取课程报名列表"
        })
    }

    /**
     * 添加学生至课程报名
     */
    static addCourseApply = async (params: {
        // md5加密（微同课+年月日时分，格式：微同课2024-12-19 00:00）
        key: string,
        // 课程id
        course_id: number,
        // 昵称
        name: string,
        // 学生手机号
        phone: string,
        // 观看课程起止时间（十位时间戳）
        view_start_time?: number,
    }) => {
        return http({
            url: "/order/add_course_apply",
            method: "POST",
            data: params,
            name: "添加单个学生至课程报名"
        })
    }

    /**
     * formData格式
     * - key: md5加密（微同课+年月日时分，格式：微同课2023-07-10 16:20）
     * - course_id: 课程id
     * - file: excel文件
     */
    static batchAddCourseApply = async (params: FormData) => {
        return http({
            url: "/order/batch_add_course_apply",
            method: 'POST',
            data: params,
            name: "批量添加学生至课程报名"
        })
    }

    /**
     * 删除学员 课程详情—报名列表处展示
     */
    static delCourseOrder = async (params: {
        // 订单id
        order_id: number,
        // 要删除订单的用户id
        user_id: number,
        // 课程id
        course_id: number,
    }) => {
        return http({
            url: "/order/del_course_order",
            method: 'POST',
            data: params,
            name: "删除学员 课程详情—报名列表处展示"
        })
    }
}

export class TestService {

    /**
     * 获取套卷首页信息
     */
    static getTestIndex = async () => {
        return http<TestIndex>({
            url: "/test/index",
            method: 'POST',
            name: "题库首页信息"
        })
    }

    /**
     * 获取题目列表
     */
    static getTestQuestionList = async (params: {
        /**
         * 一级分类id
         */
        f_id?: number,
        /**
         * 二级分类id
         */
        s_id?: number,
        /**
         * 题目id，格式：id1,id2,id3
         */
        id: string,
        /**
         * 关键字搜索
         */
        content: string,
        /**
         * 开始时间
         */
        start_time: number,
        /**
         * 结束时间
         */
        end_time: number,
        /**
         * 分页页数，默认为1
         */
        page: number,
        /**
         * 分页条数，默认为15
         */
        pageSize: number,
        /**
         * 套卷id
         */
        reel_id: number,
        /**
         * 题型；题型 0组合题 1单选 2多选 3填空 4简答 5=竞赛
         */
        type: number,
    }) => {
        return http<TestQuestionList>({
            url: "/test/question_list",
            method: 'POST',
            data: params,
            name: "获取题目列表"
        })
    }

    static getTestQuestionInfo = async (params: {
        /**
         * 题目id
         */
        id: number | string,
        /**
         * 用户id；，隔开
         */
        user_id?: string,
        /**
         * 以下均为评论区参数, 开始时间
         */
        start_time?: number,
        /**
         * 结束时间
         */
        end_time?: number,
        /**
         * 页码（此前是f_page）
         */
        page?: number,
        /**
         * 条数（此前是f_pageSize）
         */
        pageSize?: number,
        /**
         * 关键字搜索
         */
        content?: string,
        /**
         * 状态；0=下架；1=上架
         */
        status?: number
    }) => {
        return http<TestQuestionInfo>({
            url: "/test/question_info",
            method: 'POST',
            data: params,
            name: "获取题目详情"
        })
    }

    /**
     * 添加/编辑单个测试题目
     * @param params
     */
    static addEditTestQuestion = async (params: {
        /**
         * 一级分类id
         */
        f_cate_id: number,
        /**
         * 二级分类id
         */
        s_cate_id: number,
        /**
         * 题目
         */
        problem: string,
        /**
         * 答案
         */
        answer: string,
        /**
         * 正确答案
         */
        right_answer: string,
        /**
         * 图片
         */
        image?: string,
        /**
         * 解析
         */
        parsing: string,
        /**
         * 分值
         */
        score?: string,
        /**
         * 分值json
         */
        score_json?: string,
        /**
         * 答案图片
         */
        right_answer_image?: string,
        /**
         * 卷id
         */
        reel_id?: number,
        /**
         * 题型：题型 0组合题 1单选 2多选 3填空 4简答 5=竞赛
         */
        type: number,
        /**
         * 排序
         */
        sort?: number,
        /**
         * 添加组合题小题时的大题id
         */
        pid?: number,
        /**
         * 题目id，编辑时必传
         */
        id?: number
    }) => {
        return http({
            url: "/test/alter_question",
            method: 'POST',
            data: params,
            name: "添加/ 编辑单个测试题目"
        })
    }

    /**
     * 测试题目批量上传 <br />
     * <b>上传套卷时，套卷信息为必传项</b> <br />
     * f_cate_id - 一级分类id <br />
     * s_cate_id - 二级分类id <br />
     * one_cate_id - 套卷第一个分类 <br />
     * two_cate_id - 套卷第二个分类 <br />
     * three_cate_id - 套卷第三个分类 <br />
     * reel_name - 套卷名称 <br />
     * score - 套卷分值,最长为2位小数点 <br />
     * @param params
     */
    static addBatchQuestion = async (params: FormData) => {
        return http({
            url: "/test/add_batch_question",
            method: 'POST',
            data: params,
            name: "测试题目批量上传"
        })
    }

    /**
     * 测试题目一级分类
     * @param params
     */
    static getOneClassifyList = (params: {}) => {
        return http<TestCate[]>({
            url: "/test/one_classify_list",
            method: 'POST',
            data: params
        })
    }

    /**
     * 测试题库二级/三级分类
     * @param params
     */
    static getRestsClassifyList = (params: {
        /**
         * 一级分类id
         */
        cate_id: number | undefined,
        /**
         * 	层级：2/3
         */
        level: number
    }) => {
        return http<TestSubCate[]>({
            url: "/test/rests_classify_list",
            method: 'POST',
            data: params
        })
    }

    /**
     * 获取套卷分类列表
     * @param params
     */
    static getReelCateList = async (params: {
        /**
         * 题库二级分类id
         */
        cate_id: number
    }) => {
        return http<ReelCate[]>({
            url: "/test/reel_cate_list",
            method: 'POST',
            data: params,
            name: "套卷分类列表"
        })
    }

    /**
     * 获取套卷列表
     * @param params
     */
    static getReelList = async (params: {
        page: number,
        pageSize: number,
        /**
         * 一级分类
         */
        f_id?: number,
        /**
         * 二级分类
         */
        s_id?: number,
        /**
         * 是否启用，1=是，0=否
         */
        status?: number,
        /**
         * 关键字搜索
         */
        content: string,
        /**
         * 开始时间戳
         */
        start_time: number,
        /**
         * 结束时间
         */
        end_time: number
    }) => {
        return http<TestReelList>({
            url: "/test/reel_list",
            method: 'POST',
            data: params,
            name: "获取套卷列表"
        })
    }
}
